// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { normalizeCustomerDocument } from '@tectonics/prs/customer/normalize';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

export function FieldCostaRicanDocument(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [document, handleChangeDocument] = useUserInputAsTextFieldWithFormatting(
    'costaRicanDocument',
    formatRaw,
    normalizeCustomerDocument,
  );

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('costaRicanDocument');

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('document');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="customer-document">
      <label htmlFor={inputId}>Documento de Identificación</label>

      <TextField
        as={InputMask}
        id={inputId}
        type="tel"
        value={document}
        onChange={handleChangeDocument}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        mask={COSTA_RICA_MASK}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="customer-document"
      />

      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </div>
  );
}

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return <ErrorLabel>El número es invalido.</ErrorLabel>;
  }
}

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, COSTA_RICA_MASK);
}

const COSTA_RICA_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/,
  /[0-9]/, /[0-9]/, /[0-9]/,
  /[0-9]/, /[0-9]/, /[0-9]/,
];
